import React, { useState, useMemo } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	Heart,
	Reply,
	ChevronDown,
	ChevronUp,
	Pin,
	PinOff,
} from "lucide-react";
import { newsfeedApi } from "../../api/newsFeedApi";
import useNewsfeed from "../../zustand/UseNewsfeed";
import useUserStore from "../../zustand/UseUserStore";
import useAuthRedirect from "../../hooks/useAuthRedirect";

// Memoized date component for better performance
const MemoizedDate = ({
	dateString,
	className = "text-xs text-muted-foreground dark:text-muted-foreground",
}) => {
	const formattedDate = useMemo(() => {
		const date = new Date(dateString);
		const months = [
			"Jan",
			"Feb",
			"Mar",
			"Apr",
			"May",
			"Jun",
			"Jul",
			"Aug",
			"Sep",
			"Oct",
			"Nov",
			"Dec",
		];
		const month = months[date.getMonth()];
		const day = date.getDate().toString().padStart(2, "0");
		return `${month} ${day}`;
	}, [dateString]);

	return <span className={className}>{formattedDate}</span>;
};

const Comment = ({
	comment,
	replyId,
	setReplyId,
	isOwnPost,
	postType,
	itemId,
}) => {
	const [reset, setReset] = useState(false);
	const [page, setPage] = useState(1);
	const [replyInput, setReplyInput] = useState("");
	const newsfeed = useNewsfeed();
	const userStore = useUserStore();
	const authRedirect = useAuthRedirect();

	// Get paginated replies for a comment
	// Takes in id of comment and page number
	const handleGetReplies = async (commentID, page) => {
		const response = await newsfeedApi.getReplies(
			commentID,
			userStore.user.MemberID,
			page
		);
		comment.Replies = [...comment.Replies, ...response.result.replies];
		setReset(!reset);
	};

	// Get the author of the parent reply
	// Takes in id of target reply
	function getParentAuthor(targetReplyID) {
		if (comment.CommentID === targetReplyID) {
			return comment.AuthorName;
		}

		// Find comment in replies matching target id and return author
		let author = null;
		comment.Replies.forEach((r) => {
			if (r.CommentID === targetReplyID) {
				author = r.AuthorName;
				return author;
			}
		});
		return author;
	}

	// For tracking which reply we're replying to
	const [replyingToReplyId, setReplyingToReplyId] = useState(null);

	return (
		<div
			className={`h-max space-y-2 ${
				comment.IsPinned
					? "rounded-lg border border-amber-200 bg-amber-50 p-2 dark:border-amber-700 dark:bg-amber-900/20"
					: "pe-2"
			}`}
		>
			{comment.IsPinned ? (
				<div className="mb-1 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
					<Pin size={12} />
					<span>Pinned comment</span>
				</div>
			) : null}{" "}
			<div className="flex gap-2">
				<Avatar className="h-7 w-7 border border-border dark:border-border">
					<AvatarFallback className="bg-muted text-muted-foreground dark:bg-muted dark:text-muted-foreground">
						{comment.AuthorName}
					</AvatarFallback>
					<AvatarImage src={comment.AvatarURL} />
				</Avatar>
				<div className="flex-1 rounded-md border border-border bg-muted/50 p-2 dark:border-border dark:bg-muted/30">
					<div className="flex items-baseline justify-between">
						<p className="text-sm font-medium font-semibold text-foreground dark:text-foreground">
							{comment.AuthorName}
						</p>
						<MemoizedDate dateString={comment.Datetime} />
					</div>
					<p className="text-sm text-foreground dark:text-foreground">
						{comment.Comment}
					</p>
				</div>
			</div>
			{/* Comment action buttons */}
			<div className="ml-9 mt-1 flex gap-3">
				<Button
					variant="ghost"
					size="sm"
					className="flex h-6 items-center gap-1 px-2 text-xs text-muted-foreground hover:bg-muted/50 dark:text-muted-foreground dark:hover:bg-muted/50"
					onClick={(e) => {
						e.stopPropagation();
						if (!userStore.userProgress.isAuthenticated) {
							authRedirect();
							return;
						}
						newsfeed.handleLikeComment(comment.CommentID, itemId);
					}}
				>
					<Heart
						className={`h-3 w-3 transition-colors duration-200 ${
							comment.IsLiked
								? "fill-red-500 text-red-500 dark:fill-red-400 dark:text-red-400"
								: ""
						}`}
					/>
					<span>{comment.Likes.length}</span>
				</Button>

				{userStore.getAccessLevel() > 1 && (
					<Button
						variant="ghost"
						size="sm"
						className="flex h-6 items-center gap-1 px-2 text-xs text-muted-foreground hover:bg-muted/50 dark:text-muted-foreground dark:hover:bg-muted/50"
						onClick={(e) => {
							e.stopPropagation();
							setReplyId(
								replyId === comment.CommentID ? null : comment.CommentID
							);
							setReplyInput("");
							setReplyingToReplyId(null);
						}}
					>
						<Reply className="h-3 w-3" />
						<span>Reply</span>
					</Button>
				)}

				{/* Pin/Unpin button - Only available for current user's comments */}
				{isOwnPost ? (
					<Button
						variant="ghost"
						size="sm"
						className={`flex h-6 items-center gap-1 px-2 text-xs hover:bg-muted/50 dark:hover:bg-muted/50 ${
							comment.IsPinned
								? "text-amber-600 dark:text-amber-400"
								: "text-muted-foreground dark:text-muted-foreground"
						}`}
						onClick={(e) => {
							e.stopPropagation();
							newsfeed.handlePinComment(comment, itemId);
						}}
						title={
							comment.IsPinned
								? "Unpin this comment"
								: "Pin this comment to the top"
						}
					>
						{comment.IsPinned ? (
							<>
								<PinOff className="h-3 w-3" />
								<span>Unpin</span>
							</>
						) : (
							<>
								<Pin className="h-3 w-3" />
								<span>Pin</span>
							</>
						)}
					</Button>
				) : null}
			</div>
			{/* Reply form */}
			{replyId === comment.CommentID && (
				<div className="ml-9 flex gap-2 py-2">
					<Avatar className="h-6 w-6 border border-border dark:border-border">
						<AvatarFallback className="bg-muted text-muted-foreground dark:bg-muted dark:text-muted-foreground">
							{userStore.user.Username}
						</AvatarFallback>
						<AvatarImage src={userStore.user.AvatarURL || userStore.avatar} />
					</Avatar>
					<input
						type="text"
						placeholder={`Reply to ${comment.AuthorName}...`}
						className="flex-1 rounded-md border border-border bg-background px-3 py-1 text-xs text-foreground placeholder:text-muted-foreground dark:border-border dark:bg-background dark:text-foreground dark:placeholder:text-muted-foreground"
						id={`reply-input-${comment.CommentID}`}
						value={replyInput}
						onChange={(e) => setReplyInput(e.target.value)}
						onClick={(e) => e.stopPropagation()}
					/>
					<Button
						size="sm"
						className="bg-purple-600 text-xs text-white hover:bg-purple-700 dark:bg-purple-600 dark:hover:bg-purple-700"
						onClick={(e) => {
							e.stopPropagation();
							newsfeed.handleAddComment(
								itemId,
								replyInput,
								comment.CommentID,
								comment.CommentID,
								"Text",
								postType
							);
							setReplyId(null);
							setReplyInput("");
						}}
					>
						Reply
					</Button>
					<Button
						variant="ghost"
						size="sm"
						className="text-xs text-muted-foreground hover:bg-muted/50 dark:text-muted-foreground dark:hover:bg-muted/50"
						onClick={(e) => {
							e.stopPropagation();
							setReplyId(null);
							setReplyInput("");
						}}
					>
						Cancel
					</Button>
				</div>
			)}
			{comment.Replies?.map((reply, index) => (
				<div key={reply.CommentID} className="ml-9 space-y-2">
					<div className="flex gap-2">
						<Avatar className="h-6 w-6 border border-border dark:border-border">
							<AvatarFallback className="bg-muted text-muted-foreground dark:bg-muted dark:text-muted-foreground">
								{reply.AuthorName}
							</AvatarFallback>
							<AvatarImage src={reply.AvatarURL} />
						</Avatar>
						<div className="w-full">
							<div className="flex-1 rounded-md border border-border bg-muted/30 p-2 dark:border-border dark:bg-muted/20">
								<div className="flex items-baseline justify-between">
									<p className="text-xs font-medium font-semibold text-foreground dark:text-foreground">
										{reply.AuthorName} ▸{" "}
										<span className="text-muted-foreground dark:text-muted-foreground">
											{getParentAuthor(reply.TargetReplyID)}
										</span>
									</p>
									<MemoizedDate dateString={reply.Datetime} />
								</div>
								<p className="text-sm text-foreground dark:text-foreground">
									{reply.Comment}
								</p>
							</div>

							<div className="mt-1 flex gap-3">
								<Button
									variant="ghost"
									size="sm"
									className="flex h-5 items-center gap-1 px-2 text-xs text-muted-foreground hover:bg-muted/50 dark:text-muted-foreground dark:hover:bg-muted/50"
									onClick={(e) => {
										e.stopPropagation();
										if (userStore.getAccessLevel() <= 1) {
											authRedirect();
											return;
										}
										newsfeed.handleLikeComment(reply.CommentID, itemId);
									}}
								>
									<Heart
										className={`h-2 w-2 transition-colors duration-200 ${
											reply.IsLiked
												? "fill-red-500 text-red-500 dark:fill-red-400 dark:text-red-400"
												: ""
										}`}
									/>
									<span>{reply.Likes.length}</span>
								</Button>

								{userStore.getAccessLevel() > 1 && (
									<Button
										variant="ghost"
										size="sm"
										className="flex h-5 items-center gap-1 px-2 text-xs text-muted-foreground hover:bg-muted/50 dark:text-muted-foreground dark:hover:bg-muted/50"
										onClick={(e) => {
											e.stopPropagation();
											setReplyId(null);
											setReplyInput("");
											setReplyingToReplyId(
												replyingToReplyId === reply.CommentID
													? null
													: reply.CommentID
											);
										}}
									>
										<Reply className="h-2 w-2" />
										<span>Reply</span>
									</Button>
								)}
							</div>
						</div>
					</div>
					{/* Show reply form for replies */}
					{replyingToReplyId === reply.CommentID && (
						<div className="mt-2 flex gap-2 py-2">
							<Avatar className="h-6 w-6 border border-border dark:border-border">
								<AvatarFallback className="bg-muted text-muted-foreground dark:bg-muted dark:text-muted-foreground">
									{userStore.user.Username}
								</AvatarFallback>
								<AvatarImage src={userStore.avatar} />
							</Avatar>
							<div className="flex flex-1 gap-2">
								<input
									type="text"
									placeholder="Reply to thread..."
									className="flex-1 rounded-md border border-border bg-background px-3 py-1 text-xs text-foreground placeholder:text-muted-foreground dark:border-border dark:bg-background dark:text-foreground dark:placeholder:text-muted-foreground"
									onClick={(e) => e.stopPropagation()}
									id={`reply-to-reply-input-${replyingToReplyId}`}
								/>
								<Button
									size="sm"
									className="bg-primary text-xs text-primary-foreground hover:bg-primary/90 dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90"
									onClick={(e) => {
										e.stopPropagation();
										const input = document.getElementById(
											`reply-to-reply-input-${replyingToReplyId}`
										);
										newsfeed.handleAddComment(
											itemId,
											input.value,
											reply.ParentID,
											reply.CommentID,
											"Text",
											postType
										);
										input.value = "";
										setReplyingToReplyId(null);
									}}
								>
									Reply
								</Button>
								<Button
									variant="ghost"
									size="sm"
									className="text-xs text-muted-foreground hover:bg-muted/50 dark:text-muted-foreground dark:hover:bg-muted/50"
									onClick={(e) => {
										e.stopPropagation();
										setReplyingToReplyId(null);
									}}
								>
									Cancel
								</Button>
							</div>
						</div>
					)}
				</div>
			))}
			{/* Show replies toggle button */}
			{comment.ReplyCount > 0 && (
				<Button
					variant="ghost"
					size="sm"
					className="ml-9 flex h-6 items-center gap-1 text-xs text-blue-600 hover:bg-muted/50 dark:text-blue-400 dark:hover:bg-muted/50"
					onClick={(e) => {
						e.stopPropagation();
						if (comment.Replies.length < comment.ReplyCount) {
							handleGetReplies(comment.CommentID, page);
							setPage(page + 1);
						} else {
							comment.Replies = [];
							setPage(1);
						}
					}}
				>
					{comment.Replies.length >= comment.ReplyCount ? (
						<>
							<ChevronUp className="h-3 w-3" />
							<span>Hide replies</span>
						</>
					) : comment.Replies.length > 0 ? (
						<>
							<ChevronDown className="h-3 w-3" />
							<span>
								View {comment.ReplyCount - comment.Replies.length} more{" "}
								{comment.ReplyCount - comment.Replies.length === 1
									? "reply"
									: "replies"}
							</span>
						</>
					) : (
						<>
							<ChevronDown className="h-3 w-3" />
							<span>
								View {comment.ReplyCount}{" "}
								{comment.ReplyCount === 1 ? "reply" : "replies"}
							</span>
						</>
					)}
				</Button>
			)}
		</div>
	);
};

export default Comment;
